
import { muiDataGridStarted } from "../../apiutils";
import { apiSlice } from "../../createApiSlice";

const RELATION_BASE = "/relations";
const buildUrl = (endpoint) => RELATION_BASE + endpoint;
const ApiName = "Relations";
const ApiContactName = "RelationContacts";
const ApiInvoiceProductsName = "RelationInvoiceProducts";

const relationsApiSlice =  apiSlice.injectEndpoints({
    endpoints: builder => ({
        getRelations: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: (result, error, arg) => [{ type: `${ApiName}`}],
            onQueryStarted: muiDataGridStarted.bind(null, 'getRelations', `${ApiName}`)
        }),        
        getRelationContacts: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/${args.id}/contacts`),
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: ApiContactName }]
        }),
        getRelationTotals: builder.query({
            query: () => ({
                url: buildUrl(`/totals/state`),
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: `${ApiName}TotalsState` }]
        }),
        getRelationsSelect: builder.query({
            query: () => ({
                url: buildUrl(`/select`),
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: `${ApiName}Single`, id: arg }]
        }),
        exportRelations: builder.mutation({
            query: (body) => {
                return {
                  url: buildUrl(`/export`),
                  method: 'POST',
                  responseHandler: async (response) => {
                        if (!response.ok) {
                            throw new Error('Network response was not ok');
                        }
                        const blob = new Blob([await response.blob()], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                          });
                        const url = URL.createObjectURL(blob);
                        return {data: url};
                    },
                };
              },
        }),        
        getRelationInvoiceProducts: builder.query({
            query: productInfo => ({
                url: buildUrl(`/${productInfo.id}/invoiceproducts?onlyActive=${productInfo.onlyActive}${productInfo.subType ? `&subtype=${productInfo.subType}` : ""}`),
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: ApiInvoiceProductsName }]
        }),
        getAllRelationInvoiceProducts: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/${args.id}/invoiceproducts/all?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: (result, error, arg) => [{ type: `${ApiInvoiceProductsName}`}],
            onQueryStarted: muiDataGridStarted.bind(null, 'getAllRelationInvoiceProducts', `${ApiInvoiceProductsName}`)
        }),               
        updateRelationInvoiceProducts: builder.mutation({
            query: productsInfo => ({
                url: buildUrl(`/${productsInfo.relationId}/invoiceproducts`),
                method: 'PUT',
                body: productsInfo.products,
            }),
            invalidatesTags: (result, error, arg) => [{ type: ApiInvoiceProductsName }, { type: `${ApiName}Single`, id: arg }],
        }),
        toggleRelationInvoiceProductsActive: builder.mutation({
            query: ({ relationId, ids }) => ({
                url: buildUrl(`/${relationId}/invoiceproducts/toggleactive`),
                method: 'PUT',
                body: { ids },
            }),
            invalidatesTags: (result, error, arg) => [{ type: ApiInvoiceProductsName }, { type: `${ApiName}Single`, id: arg.relationId }],
        }),
        getRelation: builder.query({
            query: id => ({
                url: buildUrl(`/${id}`),
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: `${ApiName}Single`, id: arg }]
        }),
        getRelationForMenu: builder.query({
            query: id => ({
                url: buildUrl(`/${id}/menu`),
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: `${ApiName}SingleMenu`}],
            keepUnusedDataFor: 3600
        }),
        addNewRelation: builder.mutation({
            query: relationInfo => ({
                url: buildUrl(`/add`),
                method: 'POST',
                body: relationInfo,
            }),
            invalidatesTags: [`${ApiName}`],
        }),
        editRelation: builder.mutation({
            query: relationInfo => ({
                url: buildUrl(`/${relationInfo.id}`),
                method: 'PUT',
                body: relationInfo,
            }),
            invalidatesTags: (result, error, arg) => [{ type: ApiName }, {type: `${ApiName}`}, {type: 'Orders'}, { type: 'OrderTotals' }, {type: 'Orders'}, {type: 'InvoiceCategories'}, { type: ApiInvoiceProductsName }, { type: `${ApiName}Single`, id: arg.id }, { type: `${ApiName}SingleMenu`, id: arg.id }],
        }),
        deleteRelation: builder.mutation({
            query: args => ({
                url: buildUrl(`/${args.id}`),
                method: 'DELETE'
            }),
            invalidatesTags: (result, error, arg) => [{type: ApiName}],
        }),
        addRelationContact: builder.mutation({
            query: relationContactInfo => ({
                url: buildUrl(`/${relationContactInfo.relationId}/contacts`),
                method: 'POST',
                body: relationContactInfo,
            }),
            invalidatesTags: (result, error, arg) => [{type: ApiContactName}],
        }),
        updateRelationContact: builder.mutation({
            query: relationContactInfo => ({
                url: buildUrl(`/${relationContactInfo.relationId}/contacts/${relationContactInfo.id}`),
                method: 'PUT',
                body: relationContactInfo,
            }),
            invalidatesTags: (result, error, arg) => [{type: ApiContactName}],
        }),
        deleteRelationContact: builder.mutation({
            query: args => ({
                url: buildUrl(`/${args.relationId}/contacts/${args.id}`),
                method: 'DELETE'
            }),
            invalidatesTags: (result, error, arg) => [{type: ApiContactName}],
        }),
        updatePortalstyle: builder.mutation({
            query: args => {
                const { ...body } = args;
                return {
                    url: buildUrl(`/portalstyle`),
                    method: 'PUT',
                    body,
                };
            },
            invalidatesTags: (result, error, arg) => [{type: ApiName}],
        }),
        
        updatePortalLogo: builder.mutation({
            query: file => {
                const formData = new FormData();
                formData.append('image', file);
                return {
                    url: buildUrl(`/portallogo`),
                    method: 'PUT',
                    body: formData,
                };
            },
            invalidatesTags: (result, error, arg) => [{type: ApiName}, { type: `${ApiName}SingleMenu`}],
        }),
        getRelationKvk: builder.query({
            query: id => ({
                url: buildUrl(`/kvk/${id}`),
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: `${ApiName}/kvk`, id: arg }]
        }),
        syncWebshop: builder.mutation({
            query:(data) => ({
                url: buildUrl(`/${data.id}/webshop/sync/${data.type}`),
                method:'GET',
            })
        })
    })
});

export const {
    useGetRelationsQuery,
    useGetRelationQuery,
    useGetRelationContactsQuery,
    useGetRelationInvoiceProductsQuery,
    useGetAllRelationInvoiceProductsQuery,
    useUpdateRelationInvoiceProductsMutation,
    useToggleRelationInvoiceProductsActiveMutation,
    useAddNewRelationMutation,
    useEditRelationMutation,
    useAddRelationContactMutation,
    useUpdateRelationContactMutation,
    useDeleteRelationContactMutation,
    useDeleteRelationMutation,
    useGetRelationsSelectQuery,
    useUpdatePortalstyleMutation,
    useGetRelationForMenuQuery,
    useGetRelationKvkQuery,
    useSyncWebshopMutation,
    useExportRelationsMutation,
    useGetRelationTotalsQuery,
    useUpdatePortalLogoMutation,
  } = relationsApiSlice;